/**
 * SignaturePad — วาดลายเซ็นด้วยนิ้ว/เมาส์บน canvas แล้วอัปโหลดเป็นรูป PNG.
 * ใช้ pointer events (รองรับทั้งสัมผัสและเมาส์) + กันหน้าเลื่อนขณะวาด
 * (touch-action: none). อัปโหลดผ่าน uploadFile(..., 'signature') แล้วส่ง
 * R2 path กลับผ่าน onSigned.
 */
import { useEffect, useRef, useState } from 'react';
import { uploadFile } from '../../lib/api-client';
import { Eraser, Check } from 'lucide-react';

interface SignaturePadProps {
  onSigned: (r2Path: string) => void;
  onCancel?: () => void;
}

export function SignaturePad({ onSigned, onCancel }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Scale for device pixel ratio so lines stay crisp on retina/mobile screens.
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1A1714';
  }, []);

  function getPos(canvas: HTMLCanvasElement, e: React.PointerEvent): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    canvas.setPointerCapture(e.pointerId);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y } = getPos(canvas, e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    drawingRef.current = true;
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y } = getPos(canvas, e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasDrawnRef.current) {
      hasDrawnRef.current = true;
      setHasDrawn(true);
    }
  }

  function handlePointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas) canvas.releasePointerCapture(e.pointerId);
  }

  function handleClear() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawnRef.current = false;
    setHasDrawn(false);
  }

  async function handleConfirm() {
    const canvas = canvasRef.current;
    if (!canvas || !hasDrawn) return;
    setUploading(true);
    try {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('สร้างรูปลายเซ็นไม่สำเร็จ');
      const path = await uploadFile(blob, 'signature', 'signature.png');
      onSigned(path);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          className="w-full h-48 touch-none cursor-crosshair"
          style={{ touchAction: 'none' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />
      </div>
      <p className="text-xs text-gray-400 text-center">ใช้นิ้วหรือเมาส์วาดลายเซ็นในกรอบด้านบน</p>
      <div className="flex gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn-secondary flex-1">
            ยกเลิก
          </button>
        )}
        <button type="button" onClick={handleClear} className="btn-secondary flex-1">
          <Eraser className="w-4 h-4" /> ล้าง
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!hasDrawn || uploading}
          className="btn-cta flex-1"
        >
          <Check className="w-4 h-4" /> {uploading ? 'กำลังบันทึก…' : 'ยืนยันลายเซ็น'}
        </button>
      </div>
    </div>
  );
}
